import React, { useState, useEffect } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Radio } from "antd"
import { toast } from "react-toastify"
import Sidebar from "../Sidebar/Sidebar"
import BtnAdd from "../Commons/Buttons/BtnAdd"
import NumberInput from "../Commons/Forms/NumberInput"
import TextField from "../Commons/Forms/TextField"
import { getNcdAnswers, getNcdAnswersByPatientId } from "../Helpers/API/PhilhealthApi/PhilhealthApi"
import backarrow from "../../Assets/Icons/back-arrow.png"

const RISK_LEVELS = [
  { value: "A", label: "A – <10%" },
  { value: "B", label: "B – 10% to <20%" },
  { value: "C", label: "C – 20% to <30%" },
  { value: "D", label: "D – 30% to <40%" },
  { value: "E", label: "E – ≥40%" },
]

const YES_NO = [
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
]

function RadioYesNo({ name, value, onChange }) {
  return (
    <Radio.Group className="w-full mt-2" name={name} value={value} onChange={onChange}>
      {YES_NO.map((opt) => (
        <Radio key={opt.value} value={opt.value}>
          {opt.label}
        </Radio>
      ))}
    </Radio.Group>
  )
}

function SectionLabel({ children }) {
  return (
    <div
      className="mt-4 mb-2"
      style={{ fontWeight: "bold", textDecoration: "underline", fontSize: "0.95rem" }}
    >
      {children}
    </div>
  )
}

const INITIAL_STATE = {
  // Header 1 – High Fat/High Salt Food Intake
  q1_processed_food: "N",       // Q1

  // Header 2 – Dietary Fiber Intake
  q2_vegetables: "N",           // Q2
  q3_fruits: "N",               // Q3

  // Header 3 – Physical Activity
  q4_physical_activity: "N",    // Q4

  // Header 4 – Presence or Absence of Diabetes
  q5_diagnosed_diabetes: "N",   // Q5
  q6_polyphagia: "N",           // Q6
  q7_polydipsia: "N",           // Q7
  q8_polyuria: "N",             // Q8
  q18_with_medication: "",     // Q18 (child of Q5)
  q19_raised_blood_glucose: "N",// Q19
  q19_fbs_rbs_mg_dl: "",
  q19_fbs_rbs_mmol_l: "",
  q19_fbs_rbs_date: "",
  q20_raised_blood_lipids: "N", // Q20
  q20_total_cholesterol_mg_dl: "",
  q20_total_cholesterol_date: "",
  q21_urine_ketones: "N",       // Q21
  q21_urine_ketones_value: "",
  q21_urine_ketones_date: "",
  q22_urine_protein: "N",       // Q22
  q22_urine_protein_value: "",
  q22_urine_protein_date: "",

  // Header 5 – Questionnaire to Determine Probable Angina, Heart Attack, Stroke/TIA
  q9_chest_pain: "N",           // Q9
  q10_center_left_arm_pain: "N",// Q10
  q11_pain_walking_uphill: "N", // Q11
  q12_slowdown_pain: "N",       // Q12
  q13_pain_away_standing: "N",  // Q13
  q14_pain_away_10min: "N",     // Q14
  q15_severe_chest_pain: "N",   // Q15
  q16_stroke_symptoms: "N",     // Q16
  q17_risk_level: "A",          // Q17
  q23_angina_heart_attack: "N", // Q23
  q24_stroke_tia: "N",          // Q24
}

const YES_NO_FIELDS = [
  "q1_processed_food",
  "q2_vegetables",
  "q3_fruits",
  "q4_physical_activity",
  "q5_diagnosed_diabetes",
  "q6_polyphagia",
  "q7_polydipsia",
  "q8_polyuria",
  "q9_chest_pain",
  "q10_center_left_arm_pain",
  "q11_pain_walking_uphill",
  "q12_slowdown_pain",
  "q13_pain_away_standing",
  "q14_pain_away_10min",
  "q15_severe_chest_pain",
  "q16_stroke_symptoms",
  "q19_raised_blood_glucose",
  "q20_raised_blood_lipids",
  "q21_urine_ketones",
  "q22_urine_protein",
  "q23_angina_heart_attack",
  "q24_stroke_tia",
]

const inFlightNcdqRequests = new Set()

export const getNcdqBooleanValue = (value) => (value === undefined || value === null ? "N" : value)
export const getNcdqRiskLevel = (value) => (value === undefined || value === null ? "A" : value)

const isYesNoAnswered = (value) => value === "Y" || value === "N"
const hasText = (value) => String(value ?? "").trim() !== ""

const isNcdqFormComplete = (form) => {
  if (!YES_NO_FIELDS.every((key) => isYesNoAnswered(form[key]))) {
    return false
  }

  if (!form.q17_risk_level) {
    return false
  }

  if (form.q5_diagnosed_diabetes === "Y") {
    if (
      form.q18_with_medication !== "with_medication" &&
      form.q18_with_medication !== "without_medication"
    ) {
      return false
    }
  }

  if (form.q19_raised_blood_glucose === "Y") {
    if (
      !hasText(form.q19_fbs_rbs_mg_dl) ||
      !hasText(form.q19_fbs_rbs_mmol_l) ||
      !hasText(form.q19_fbs_rbs_date)
    ) {
      return false
    }
  }

  if (form.q20_raised_blood_lipids === "Y") {
    if (
      !hasText(form.q20_total_cholesterol_mg_dl) ||
      !hasText(form.q20_total_cholesterol_date)
    ) {
      return false
    }
  }

  if (form.q21_urine_ketones === "Y") {
    if (
      !hasText(form.q21_urine_ketones_value) ||
      !hasText(form.q21_urine_ketones_date)
    ) {
      return false
    }
  }

  if (form.q22_urine_protein === "Y") {
    if (
      !hasText(form.q22_urine_protein_value) ||
      !hasText(form.q22_urine_protein_date)
    ) {
      return false
    }
  }

  return true
}

function NewPatientNCDQ() {
  const { id } = useParams()
  const navigate = useNavigate()
  const state = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [form, setForm] = useState(INITIAL_STATE)

useEffect(() => {
  if (!id) return
  if (inFlightNcdqRequests.has(id)) return

  inFlightNcdqRequests.add(id)

  async function fetchNcdAnswers() {
    try {
      const response = await getNcdAnswersByPatientId(id)
      if (response?.data) {
        const d = response.data.data
        setForm((prev) => ({
          ...prev,
          q1_processed_food:           getNcdqBooleanValue(d.pQid1_Yn),
          q2_vegetables:               getNcdqBooleanValue(d.pQid2_Yn),
          q3_fruits:                   getNcdqBooleanValue(d.pQid3_Yn),
          q4_physical_activity:        getNcdqBooleanValue(d.pQid4_Yn),
          q5_diagnosed_diabetes:       getNcdqBooleanValue(d.pQid5_Ynx),
          q6_polyphagia:               getNcdqBooleanValue(d.pQid6_Yn),
          q7_polydipsia:               getNcdqBooleanValue(d.pQid7_Yn),
          q8_polyuria:                 getNcdqBooleanValue(d.pQid8_Yn),
          q9_chest_pain:               getNcdqBooleanValue(d.pQid9_Yn),
          q10_center_left_arm_pain:    getNcdqBooleanValue(d.pQid10_Yn),
          q11_pain_walking_uphill:     getNcdqBooleanValue(d.pQid11_Yn),
          q12_slowdown_pain:           getNcdqBooleanValue(d.pQid12_Yn),
          q13_pain_away_standing:      getNcdqBooleanValue(d.pQid13_Yn),
          q14_pain_away_10min:         getNcdqBooleanValue(d.pQid14_Yn),
          q15_severe_chest_pain:       getNcdqBooleanValue(d.pQid15_Yn),
          q16_stroke_symptoms:         getNcdqBooleanValue(d.pQid16_Yn),
          q17_risk_level:              getNcdqRiskLevel(d.pQid17_Abcde),
          q18_with_medication:         d.pQid18_Yn === "Y"
                                         ? "with_medication"
                                         : d.pQid18_Yn === "N"
                                         ? "without_medication"
                                         : "",
          q19_raised_blood_glucose:    getNcdqBooleanValue(d.pQid19_Yn),
          q19_fbs_rbs_mg_dl:           d.pQid19_Fbsmg    ?? "",
          q19_fbs_rbs_mmol_l:          d.pQid19_Fbsmmol  ?? "",
          q19_fbs_rbs_date:            d.pQid19_Fbsdate  ?? "",
          q20_raised_blood_lipids:     getNcdqBooleanValue(d.pQid20_Yn),
          q20_total_cholesterol_mg_dl: d.pQid20_Choleval  ?? "",
          q20_total_cholesterol_date:  d.pQid20_Choledate ?? "",
          q21_urine_ketones:           getNcdqBooleanValue(d.pQid21_Yn),
          q21_urine_ketones_value:     d.pQid21_Ketonval  ?? "",
          q21_urine_ketones_date:      d.pQid21_Ketondate ?? "",
          q22_urine_protein:           getNcdqBooleanValue(d.pQid22_Yn),
          q22_urine_protein_value:     d.pQid22_Proteinval  ?? "",
          q22_urine_protein_date:      d.pQid22_Proteindate ?? "",
          q23_angina_heart_attack:     getNcdqBooleanValue(d.pQid23_Yn),
          q24_stroke_tia:              getNcdqBooleanValue(d.pQid24_Yn),
        }))
      }
    } finally {
      inFlightNcdqRequests.delete(id)
    }
  }

  fetchNcdAnswers()
}, [id])
  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => {
      if (name === "q19_raised_blood_glucose" && value !== "Y") {
        return {
          ...prev,
          [name]: value,
          q19_fbs_rbs_mg_dl: "",
          q19_fbs_rbs_mmol_l: "",
          q19_fbs_rbs_date: "",
        }
      }
      if (name === "q20_raised_blood_lipids" && value !== "Y") {
        return {
          ...prev,
          [name]: value,
          q20_total_cholesterol_mg_dl: "",
          q20_total_cholesterol_date: "",
        }
      }
      if (name === "q21_urine_ketones" && value !== "Y") {
        return {
          ...prev,
          [name]: value,
          q21_urine_ketones_value: "",
          q21_urine_ketones_date: "",
        }
      }
      if (name === "q22_urine_protein" && value !== "Y") {
        return {
          ...prev,
          [name]: value,
          q22_urine_protein_value: "",
          q22_urine_protein_date: "",
        }
      }
      return { ...prev, [name]: value }
    })
  }

  const canProceed = isNcdqFormComplete(form)

  async function handleSubmit() {
    if (!isNcdqFormComplete(form)) {
      toast.error("Please answer all required questions before continuing.")
      return
    }
    setIsLoading(true)
    try {
      const payload = {
        pQid1_Yn: form.q1_processed_food,
        pQid2_Yn: form.q2_vegetables,
        pQid3_Yn: form.q3_fruits,
        pQid4_Yn: form.q4_physical_activity,
        pQid5_Ynx: form.q5_diagnosed_diabetes,
        pQid6_Yn: form.q6_polyphagia,
        pQid7_Yn: form.q7_polydipsia,
        pQid8_Yn: form.q8_polyuria,
        pQid9_Yn: form.q9_chest_pain,
        pQid10_Yn: form.q10_center_left_arm_pain,
        pQid11_Yn: form.q11_pain_walking_uphill,
        pQid12_Yn: form.q12_slowdown_pain,
        pQid13_Yn: form.q13_pain_away_standing,
        pQid14_Yn: form.q14_pain_away_10min,
        pQid15_Yn: form.q15_severe_chest_pain,
        pQid16_Yn: form.q16_stroke_symptoms,
        pQid17_Abcde: form.q17_risk_level,
        pQid18_Yn:
          form.q18_with_medication === "with_medication"
            ? "Y"
            : form.q18_with_medication === "without_medication"
            ? "N"
            : "",
        pQid19_Yn: form.q19_raised_blood_glucose,
        pQid19_Fbsmg: form.q19_fbs_rbs_mg_dl,
        pQid19_Fbsmmol: form.q19_fbs_rbs_mmol_l,
        pQid19_Fbsdate: form.q19_fbs_rbs_date,
        pQid20_Yn: form.q20_raised_blood_lipids,
        pQid20_Choleval: form.q20_total_cholesterol_mg_dl,
        pQid20_Choledate: form.q20_total_cholesterol_date,
        pQid21_Yn: form.q21_urine_ketones,
        pQid21_Ketonval: form.q21_urine_ketones_value,
        pQid21_Ketondate: form.q21_urine_ketones_date,
        pQid22_Yn: form.q22_urine_protein,
        pQid22_Proteinval: form.q22_urine_protein_value,
        pQid22_Proteindate: form.q22_urine_protein_date,
        pQid23_Yn: form.q23_angina_heart_attack,
        pQid24_Yn: form.q24_stroke_tia,
      }

      const response = await getNcdAnswers(id, payload)
      if (response?.error) {
        toast.error(
          response?.error?.data?.messages?.error_messages?.[0] ??
            response?.error?.data?.message ??
            "An error occurred"
        )
        setIsLoading(false)
        return
      }

      toast.success(response?.data?.message ?? "NCDQ saved successfully.")
      navigate("/patients/physical-examination-yakap/" + id, {
        state: {
          patient_id: id,
          fromConsult: state?.state?.fromConsult || false,
          queue_id: state?.state?.queueId || "",
          queue_no: state?.state?.queueNo || "",
          fromRegisterPatient: state?.state?.fromRegisterPatient || false,
          ncdq: form,
          ncdqApiResponse: response?.data,
        },
      })
    } catch (err) {
      console.error(err)
      toast.error("An unexpected error occurred.")
      setIsLoading(false)
    }
  }

  return (
    <div className="App">
      <Sidebar />
      <div className="profile-container col p-5 pt-4">
        <div className="row pt-2 p-3">
          <div className="col-1 text-center mt-2">
            <img
              src={backarrow}
              alt="back"
              className="backarrow"
              width={25}
              height={25}
              onClick={() => navigate("/patients/vitals/" + id)}
            ></img>
          </div>
          <div className="col font-big font-bold">PhilHealth Non-Communicable Disease Questionnaire (NCDQ)</div>
        </div>

        <div className="ml-7">
          <div className="row">
            <div className="col-sm-11">

              {/* ── HEADER 1: High Fat / High Salt Food Intake ── */}
              <SectionLabel>A. High Fat / High Salt Food Intake</SectionLabel>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    1. Eats processed/fast foods (e.g. instant noodles, hamburgers, fries, fried
                    chicken skin, etc.) and ihaw-ihaw (e.g. isaw, adidas, etc.) weekly?
                  </p>
                  <RadioYesNo name="q1_processed_food" value={form.q1_processed_food} onChange={handleChange} />
                </div>
              </div>

              {/* ── HEADER 2: Dietary Fiber Intake ── */}
              <SectionLabel>B. Dietary Fiber Intake</SectionLabel>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    2. 3 servings of vegetables daily?
                  </p>
                  <RadioYesNo name="q2_vegetables" value={form.q2_vegetables} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    3. 2-3 servings of fruits daily?
                  </p>
                  <RadioYesNo name="q3_fruits" value={form.q3_fruits} onChange={handleChange} />
                </div>
              </div>

              {/* ── HEADER 3: Physical Activity ── */}
              <SectionLabel>C. Physical Activity</SectionLabel>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    4. Does at least 2.5 hours a week of moderate-intensity physical activity?
                  </p>
                  <RadioYesNo name="q4_physical_activity" value={form.q4_physical_activity} onChange={handleChange} />
                </div>
              </div>

              {/* ── HEADER 4: Presence or Absence of Diabetes ── */}
              <SectionLabel>D. Presence or Absence of Diabetes</SectionLabel>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    5. Was patient diagnosed as having diabetes?
                  </p>
                  <RadioYesNo name="q5_diagnosed_diabetes" value={form.q5_diagnosed_diabetes} onChange={handleChange} />
                </div>
              </div>

              {/* Q18 – child of Q5 */}
              {form.q5_diagnosed_diabetes === "Y" && (
                <div className="row mt-3">
                  <div className="col">
                    <p className="form-label align-left">
                      &nbsp;&nbsp;18. If yes, choose with/without medication:
                    </p>
                    <Radio.Group
                      className="w-full mt-2"
                      name="q18_with_medication"
                      value={form.q18_with_medication}
                      onChange={handleChange}
                    >
                      <Radio value="with_medication">With Medication</Radio>
                      <Radio value="without_medication">Without Medication</Radio>
                    </Radio.Group>
                  </div>
                </div>
              )}

              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    6. Does patient have symptoms of Polyphagia?
                  </p>
                  <RadioYesNo name="q6_polyphagia" value={form.q6_polyphagia} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    7. Does patient have symptoms of Polydipsia?
                  </p>
                  <RadioYesNo name="q7_polydipsia" value={form.q7_polydipsia} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    8. Does patient have symptoms of Polyuria?
                  </p>
                  <RadioYesNo name="q8_polyuria" value={form.q8_polyuria} onChange={handleChange} />
                </div>
              </div>

              {/* Lab results */}
              <div className="row mt-3">
                <div className="col-12">
                  <p className="form-label align-left">19. Raised Blood Glucose?</p>
                  <RadioYesNo name="q19_raised_blood_glucose" value={form.q19_raised_blood_glucose} onChange={handleChange} />
                </div>
                {form.q19_raised_blood_glucose === "Y" && (
                  <div className="col-12">
                    <div className="row">
                      <div className="col-sm-3">
                        <TextField
                          formLabel="FBS/RBS in mg/dL"
                          name="q19_fbs_rbs_mg_dl"
                          value={form.q19_fbs_rbs_mg_dl}
                          onChange={handleChange}
                          placeholder="Enter mg/dL"
                        />
                      </div>
                      <div className="col-sm-3">
                        <TextField
                          formLabel="FBS/RBS in mmol/L"
                          name="q19_fbs_rbs_mmol_l"
                          value={form.q19_fbs_rbs_mmol_l}
                          onChange={handleChange}
                          placeholder="Enter mmol/L"
                        />
                      </div>
                      <div className="col-sm-3">
                        <div className="mb-3">
                          <label className="form-label align-left">
                            Date when the FBS/RBS value was taken
                          </label>
                          <input
                            type="date"
                            name="q19_fbs_rbs_date"
                            value={form.q19_fbs_rbs_date}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <p className="form-label align-left">20. Raised Blood Lipids?</p>
                  <RadioYesNo name="q20_raised_blood_lipids" value={form.q20_raised_blood_lipids} onChange={handleChange} />
                </div>
                {form.q20_raised_blood_lipids === "Y" && (
                  <div className="col-12">
                    <div className="row">
                      <div className="col-sm-3">
                        <TextField
                          formLabel="Total Cholesterol value in mg/dL"
                          name="q20_total_cholesterol_mg_dl"
                          value={form.q20_total_cholesterol_mg_dl}
                          onChange={handleChange}
                          placeholder="Enter mg/dL"
                        />
                      </div>
                      <div className="col-sm-3">
                        <div className="mb-3">
                          <label className="form-label align-left">
                            Date when the total cholesterol value was taken
                          </label>
                          <input
                            type="date"
                            name="q20_total_cholesterol_date"
                            value={form.q20_total_cholesterol_date}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <p className="form-label align-left">21. Presence of Urine Ketones?</p>
                  <RadioYesNo name="q21_urine_ketones" value={form.q21_urine_ketones} onChange={handleChange} />
                </div>
                {form.q21_urine_ketones === "Y" && (
                  <div className="col-12">
                    <div className="row">
                      <div className="col-sm-3">
                        <TextField
                          formLabel="Urine Ketones Value"
                          name="q21_urine_ketones_value"
                          value={form.q21_urine_ketones_value}
                          onChange={handleChange}
                          placeholder="Enter value"
                        />
                      </div>
                      <div className="col-sm-3">
                        <div className="mb-3">
                          <label className="form-label align-left">
                            Date when the urine ketones value was taken
                          </label>
                          <input
                            type="date"
                            name="q21_urine_ketones_date"
                            value={form.q21_urine_ketones_date}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <p className="form-label align-left">22. Presence of Urine Protein?</p>
                  <RadioYesNo name="q22_urine_protein" value={form.q22_urine_protein} onChange={handleChange} />
                </div>
                {form.q22_urine_protein === "Y" && (
                  <div className="col-12">
                    <div className="row">
                      <div className="col-sm-3">
                        <TextField
                          formLabel="Urine Protein Value in mg/dL"
                          name="q22_urine_protein_value"
                          value={form.q22_urine_protein_value}
                          onChange={handleChange}
                          placeholder="Enter mg/dL"
                        />
                      </div>
                      <div className="col-sm-3">
                        <div className="mb-3">
                          <label className="form-label align-left">
                            Date when the urine protein value was taken
                          </label>
                          <input
                            type="date"
                            name="q22_urine_protein_date"
                            value={form.q22_urine_protein_date}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── HEADER 5: Angina / Heart Attack / Stroke ── */}
              <SectionLabel>
                E. Questionnaire to Determine Probable Angina, Heart Attack, Stroke/TIA
              </SectionLabel>

              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    9. Have you had any pain or discomfort or any pressure or heaviness in your chest?
                  </p>
                  <RadioYesNo name="q9_chest_pain" value={form.q9_chest_pain} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    10. Do you get the pain in the center of the chest or left arm?
                  </p>
                  <RadioYesNo name="q10_center_left_arm_pain" value={form.q10_center_left_arm_pain} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    11. Do you get it when you walk uphill or hurry?
                  </p>
                  <RadioYesNo name="q11_pain_walking_uphill" value={form.q11_pain_walking_uphill} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    12. Do you slowdown if you get the pain while walking?
                  </p>
                  <RadioYesNo name="q12_slowdown_pain" value={form.q12_slowdown_pain} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    13. Does the pain go away if you stand still or if you take a tablet under the tongue?
                  </p>
                  <RadioYesNo name="q13_pain_away_standing" value={form.q13_pain_away_standing} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    14. Does the pain away in less than 10 minutes?
                  </p>
                  <RadioYesNo name="q14_pain_away_10min" value={form.q14_pain_away_10min} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    15. Have you ever had a severe chest pain across the front of your chest lasting
                    for half an hour or more?
                  </p>
                  <RadioYesNo name="q15_severe_chest_pain" value={form.q15_severe_chest_pain} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    16. Have you ever had any of the following: difficulty in talking, weakness of
                    arm and/or leg on one side of the body, or numbness on one side of the body?
                  </p>
                  <RadioYesNo name="q16_stroke_symptoms" value={form.q16_stroke_symptoms} onChange={handleChange} />
                </div>
              </div>

              {/* Q17 – Risk Level */}
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    17. Risk level percentage:
                  </p>
                  <Radio.Group
                    className="w-full mt-2"
                    name="q17_risk_level"
                    value={form.q17_risk_level}
                    onChange={handleChange}
                  >
                    {RISK_LEVELS.map((rl) => (
                      <Radio key={rl.value} value={rl.value}>
                        {rl.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </div>
              </div>

              {/* Q23 & Q24 */}
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    23. Angina or Heart Attack?
                  </p>
                  <RadioYesNo name="q23_angina_heart_attack" value={form.q23_angina_heart_attack} onChange={handleChange} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col">
                  <p className="form-label align-left">
                    24. Stroke and TIA?
                  </p>
                  <RadioYesNo name="q24_stroke_tia" value={form.q24_stroke_tia} onChange={handleChange} />
                </div>
              </div>

              <hr className="mt-4" />
            </div>
          </div>

          {/* Submit */}
          <div className="row justify-content-end mt-5 mb-3">
            <div className="col-2">
              <BtnAdd
                label={"Next"}
                size={"sm"}
                onClick={handleSubmit}
                disabled={!canProceed || isLoading}
                isClicked={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewPatientNCDQ